import { create } from 'zustand'
import type { ApplyPayload, ReviewData } from '../components/library/ResolveReview'
import { api, ApiRequestError } from '../lib/api'

// ────────────────────────────────────────────────────────────────────────────
// Types
// ────────────────────────────────────────────────────────────────────────────

export type FreeformStep =
  | 'input'
  | 'extracting'
  | 'interrogation'
  | 'review'
  | 'resolve'
  | 'committing'
  | 'complete'

export interface QuestionResult {
  done: true
}

export interface ActiveQuestion {
  done?: false
  temp_id: string
  question_text: string
  question_number: number
  max_questions: number
  item_label: string
}

export interface CandidateReview {
  temp_id: string
  employer: string
  role_title: string
  period: string | null
  authority_notes: string | null
  role_context: string | null
  competency_tags: string[]
  context_tags: string[]
  is_current: boolean
  possible_match?: { employer: string; role_title: string } | null
}

export interface DateConfirmation {
  temp_id: string
  confirmed: boolean
  period?: string
}

export interface CommitResult {
  added_count: number
  enriched_count: number
}

interface FreeformState {
  step: FreeformStep
  sessionId: string | null
  candidateCount: number
  thinCount: number
  richCount: number
  currentQuestion: ActiveQuestion | null
  candidates: CandidateReview[]
  selectedIds: Set<string>
  pendingDateConfirmations: CandidateReview[]  // candidates needing date confirmation
  dateConfirmations: DateConfirmation[]
  review: ReviewData | null  // resolved clusters staged for the resolve screen
  result: CommitResult | null
  loading: boolean
  error: string | null
}

interface FreeformActions {
  start: (text: string) => Promise<void>
  fetchNextQuestion: () => Promise<void>
  submitAnswer: (tempId: string, answer: string) => Promise<void>
  skipQuestion: (tempId: string) => Promise<void>
  fetchReview: () => Promise<void>
  toggleSelect: (tempId: string) => void
  selectAll: () => void
  confirmDate: (tempId: string, confirmed: boolean, period?: string) => void
  resolve: () => Promise<void>
  apply: (payload: ApplyPayload) => Promise<void>
  reset: () => void
}

// ────────────────────────────────────────────────────────────────────────────
// Store
// ────────────────────────────────────────────────────────────────────────────

const initialState: FreeformState = {
  step: 'input',
  sessionId: null,
  candidateCount: 0,
  thinCount: 0,
  richCount: 0,
  currentQuestion: null,
  candidates: [],
  selectedIds: new Set(),
  pendingDateConfirmations: [],
  dateConfirmations: [],
  review: null,
  result: null,
  loading: false,
  error: null,
}

export const useFreeformStore = create<FreeformState & FreeformActions>((set, get) => ({
  ...initialState,

  start: async (text: string) => {
    set({ loading: true, error: null, step: 'extracting' })
    try {
      const res = await api.post<{
        session_id: string
        candidate_count: number
        thin_count: number
        rich_count: number
      }>('/api/v1/freeform/start', { text })

      set({
        sessionId: res.session_id,
        candidateCount: res.candidate_count,
        thinCount: res.thin_count,
        richCount: res.rich_count,
        loading: false,
      })

      if (res.thin_count > 0) {
        // Enter interrogation — fetch first question
        set({ step: 'interrogation' })
        await get().fetchNextQuestion()
      } else {
        // No thin candidates — go straight to review
        await get().fetchReview()
      }
    } catch (err) {
      set({
        loading: false,
        step: 'input',
        error: err instanceof ApiRequestError ? err.error.message : 'Something went wrong.',
      })
    }
  },

  fetchNextQuestion: async () => {
    const { sessionId } = get()
    if (!sessionId) return
    set({ loading: true, error: null })
    try {
      const res = await api.get<ActiveQuestion | QuestionResult>(
        `/api/v1/freeform/${sessionId}/next-question`
      )
      if ('done' in res && res.done) {
        set({ currentQuestion: null, loading: false })
        await get().fetchReview()
      } else {
        set({ currentQuestion: res as ActiveQuestion, loading: false })
      }
    } catch (err) {
      set({
        loading: false,
        error: err instanceof ApiRequestError ? err.error.message : 'Something went wrong.',
      })
    }
  },

  submitAnswer: async (tempId: string, answer: string) => {
    const { sessionId } = get()
    if (!sessionId) return
    set({ loading: true, error: null })
    try {
      const res = await api.post<ActiveQuestion | QuestionResult>(
        `/api/v1/freeform/${sessionId}/answer`,
        { temp_id: tempId, answer }
      )
      if ('done' in res && res.done) {
        set({ currentQuestion: null, loading: false })
        await get().fetchReview()
      } else {
        set({ currentQuestion: res as ActiveQuestion, loading: false })
      }
    } catch (err) {
      set({
        loading: false,
        error: err instanceof ApiRequestError ? err.error.message : 'Something went wrong.',
      })
    }
  },

  skipQuestion: async (tempId: string) => {
    const { sessionId } = get()
    if (!sessionId) return
    set({ loading: true, error: null })
    try {
      const res = await api.post<ActiveQuestion | QuestionResult>(
        `/api/v1/freeform/${sessionId}/skip`,
        { temp_id: tempId }
      )
      if ('done' in res && res.done) {
        set({ currentQuestion: null, loading: false })
        await get().fetchReview()
      } else {
        set({ currentQuestion: res as ActiveQuestion, loading: false })
      }
    } catch (err) {
      set({
        loading: false,
        error: err instanceof ApiRequestError ? err.error.message : 'Something went wrong.',
      })
    }
  },

  fetchReview: async () => {
    const { sessionId } = get()
    if (!sessionId) return
    set({ loading: true, error: null })
    try {
      const res = await api.get<{ candidates: CandidateReview[] }>(
        `/api/v1/freeform/${sessionId}/review`
      )
      // All candidates selected by default
      const allIds = new Set(res.candidates.map((c) => c.temp_id))
      set({
        candidates: res.candidates,
        selectedIds: allIds,
        step: 'review',
        loading: false,
      })
    } catch (err) {
      set({
        loading: false,
        error: err instanceof ApiRequestError ? err.error.message : 'Something went wrong.',
      })
    }
  },

  toggleSelect: (tempId: string) => {
    const ids = new Set(get().selectedIds)
    if (ids.has(tempId)) {
      ids.delete(tempId)
    } else {
      ids.add(tempId)
    }
    set({ selectedIds: ids })
  },

  selectAll: () => {
    const allIds = new Set(get().candidates.map((c) => c.temp_id))
    set({ selectedIds: allIds })
  },

  confirmDate: (tempId: string, confirmed: boolean, period?: string) => {
    const existing = get().dateConfirmations.filter((dc) => dc.temp_id !== tempId)
    set({ dateConfirmations: [...existing, { temp_id: tempId, confirmed, period }] })
  },

  // Reconcile the selected candidates against the library and stage the resolved
  // clusters for the resolve screen (nothing is written yet).
  resolve: async () => {
    const { sessionId, selectedIds, dateConfirmations, loading } = get()
    if (!sessionId || loading) return  // guard double-submit
    set({ loading: true, error: null })
    try {
      const res = await api.post<
        | { status: 'review'; review: ReviewData }
        | { status: 'pending_date_confirmation'; candidates: CandidateReview[] }
      >(`/api/v1/freeform/${sessionId}/resolve`, {
        selected_ids: Array.from(selectedIds),
        date_confirmations: dateConfirmations.length ? dateConfirmations : undefined,
      })

      if (res.status === 'pending_date_confirmation') {
        set({ step: 'review', loading: false, pendingDateConfirmations: res.candidates })
        return
      }

      set({ review: res.review, step: 'resolve', loading: false })
    } catch (err) {
      set({
        loading: false,
        step: 'review',
        error: err instanceof ApiRequestError ? err.error.message : 'Something went wrong.',
      })
    }
  },

  // Commit the user-confirmed dispositions from the resolve screen.
  apply: async (payload: ApplyPayload) => {
    const { sessionId, loading } = get()
    if (!sessionId || loading) return  // guard double-submit
    set({ step: 'committing', loading: true, error: null })
    try {
      const res = await api.post<{
        added_count: number
        enriched_count: number
        framing_count: number
      }>(`/api/v1/freeform/${sessionId}/apply`, payload)

      set({
        result: {
          added_count: res.added_count,
          enriched_count: res.enriched_count + res.framing_count,
        },
        step: 'complete',
        loading: false,
      })
    } catch (err) {
      set({
        loading: false,
        step: 'resolve',
        error: err instanceof ApiRequestError ? err.error.message : 'Something went wrong.',
      })
    }
  },

  reset: () => set({ ...initialState, selectedIds: new Set() }),
}))
